/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { classNames } from '../utils/UNSAFE_classNames';
import { MessageSeverity } from './TrainIcons.types';
import { useContext } from 'preact/hooks';
import { Context, TrainContextType } from './TrainContext';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';

import type { BundleType } from '../resources/nls/bundle';

import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TrainVariantOptions, TrainStyles } from './themes/TrainStyles.css';
import { TrainRedwoodTheme } from './themes/redwood/TrainTheme';

interface StepLabelProps {
  /**
   * Determines if the step is disabled or not
   */
  isDisabled: boolean;
  /**
   * Determines if the step is visited or not
   */
  isVisited: boolean;

  /**
   * Label of the step
   */
  label: string;

  /**
   * Determines the message type icon from the following types 'error', 'warning', 'confirmation', 'info' and 'none'.
   */
  messageType?: Exclude<MessageSeverity, 'none'>;
}

/**
 * Step label component
 */
export const StepLabel = ({ label, isDisabled, isVisited, messageType }: StepLabelProps) => {
  const { numberOfSteps, index, selectedStepIndex } = useContext<TrainContextType>(Context);

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const isCurrent = selectedStepIndex === index;

  const { interactionProps, applyPseudoHoverStyle, applyHoverStyle } = useInteractionStyle(
    isDisabled || isCurrent
  );

  const currentStepStatus = isCurrent
    ? translations.train_current()
    : isVisited
    ? translations.train_visited()
    : translations.train_not_visited();

  const currentStep = index + 1;

  const statusTextForCurrentStep = messageType
    ? `${translations.train_status({
        currentStep: currentStep.toString(),
        numberOfSteps: numberOfSteps.toString()
      })}. ${currentStepStatus}, ${translations.train_message_type()}: ${messageType}`
    : `${translations.train_status({
        currentStep: currentStep.toString(),
        numberOfSteps: numberOfSteps.toString()
      })}. ${currentStepStatus}`;

  const { styles, variantClasses } = useComponentTheme<TrainVariantOptions, TrainStyles>(
    TrainRedwoodTheme,
    {
      stepLabelDisabled: isDisabled ? 'isDisabled' : 'notDisabled',
      stepLabelCurrent: isCurrent ? 'isCurrent' : 'notCurrent',
      stepLabelVisited: isVisited ? 'isVisited' : 'notVisited',
      stepLabelNeedsEventsHover: applyHoverStyle ? 'isNeedsEventsHover' : 'notNeedsEventsHover',
      stepLabelPseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover'
    }
  );

  const handleClick = (event: Event) => {
    event.preventDefault();
  };

  return (
    <span class={styles.stepLabelWrapperStyle}>
      <a
        {...interactionProps}
        href="#"
        class={classNames([styles.stepLabelBaseStyle, variantClasses])}
        onClick={handleClick}>
        {label}
        <HiddenAccessible>{statusTextForCurrentStep}</HiddenAccessible>
      </a>
    </span>
  );
};
